import { Call } from './Call';
import { VideoApi } from './gen/coordinator/video/VideoApi';
import { ApiClient } from './coordinator/connection/api-client';
import { StreamClient } from './coordinator/connection/client';
import { CallingState, ClientState } from './store';
import type {
  CallCreatedEvent,
  CallRingEvent,
  ConnectedEvent,
  QueryAggregateCallStatsRequest,
  QueryCallStatsRequest,
  QueryCallsRequest,
} from './gen/coordinator';
import type {
  CreateDeviceRequest,
  CreateGuestRequest,
  ListDevicesResponse,
  Response,
} from './gen/shims';
import {
  AllClientEvents,
  ClientEventListener,
  ErrorFromResponse,
  StreamClientOptions,
  TokenOrProvider,
  User,
  UserWithId,
} from './coordinator/connection/types';
import type { StreamVideoClientOptions } from './types';
import { retryInterval, sleep } from './coordinator/connection/utils';
import {
  createCoordinatorClient,
  createTokenOrProvider,
  getCallInitConcurrencyTag,
  getInstanceKey,
} from './helpers/clientUtils';
import { logToConsole, ScopedLogger, videoLoggerSystem } from './logger';
import { withoutConcurrency } from './helpers/concurrency';
import { enableTimerWorker } from './timers';
import { ClientEventReporter } from './reporting';
import { reconcileRingState } from './ringing';

/**
 * A `StreamVideoClient` instance lets you communicate with our API, and authenticate users.
 */
const DEVICES_PATH = '/api/v2/devices';

export class StreamVideoClient {
  /**
   * The reactive state of this client.
   * You can subscribe to changes of the different state variables.
   */
  readonly state = new ClientState();
  readonly logger: ScopedLogger;

  /**
   * The generated API for all coordinator operations.
   */
  readonly api: VideoApi;

  readonly streamClient: StreamClient;
  private readonly apiClient: ApiClient;
  private readonly clientEventReporter: ClientEventReporter;

  private effectsRegistered = false;
  private eventHandlersToUnregister: Array<() => void> = [];
  private readonly connectionConcurrencyTag = Symbol(
    'connectionConcurrencyTag',
  );

  private static _instances = new Map<string, StreamVideoClient>();
  private rejectCallWhenBusy = false;

  /**
   * You should create only one instance of `StreamVideoClient`.
   */
  constructor(apiKey: string, opts?: StreamClientOptions);
  constructor(args: StreamVideoClientOptions);
  constructor(
    apiKeyOrArgs: string | StreamVideoClientOptions,
    opts?: StreamClientOptions,
  ) {
    const apiKey =
      typeof apiKeyOrArgs === 'string' ? apiKeyOrArgs : apiKeyOrArgs.apiKey;
    const clientOptions =
      typeof apiKeyOrArgs === 'string' ? opts : apiKeyOrArgs.options;

    if (clientOptions?.enableTimerWorker) enableTimerWorker();

    const { default: defaultLogOptions, ...scopedLogOptions } =
      clientOptions?.logOptions ?? {};
    videoLoggerSystem.configureLoggers({
      ...scopedLogOptions,
      default: { sink: logToConsole, level: 'warn', ...defaultLogOptions },
    });

    const streamClient = createCoordinatorClient(apiKey, clientOptions);
    this.streamClient = streamClient;
    this.apiClient = new ApiClient(streamClient);
    this.api = new VideoApi(this.apiClient);

    this.logger = videoLoggerSystem.getLogger('client');
    this.rejectCallWhenBusy = clientOptions?.rejectCallWhenBusy ?? false;

    this.clientEventReporter = new ClientEventReporter({
      streamClient: this.streamClient,
      enabled: clientOptions?.clientEventsReportingEnabled ?? true,
    });

    if (typeof apiKeyOrArgs !== 'string' && apiKeyOrArgs.user) {
      const user = apiKeyOrArgs.user;
      if (user.type === 'anonymous') user.id = '!anon';
      if (user.id) this.registerClientInstance(apiKey, user);

      const tokenOrProvider = createTokenOrProvider(apiKeyOrArgs);
      this.connectUser(user, tokenOrProvider).catch((err) => {
        this.logger.error('Failed to connect', err);
      });
    }
  }

  /**
   * Gets or creates a StreamVideoClient instance based on the given options.
   */
  static getOrCreateInstance(
    args: StreamVideoClientOptions & { user: User },
  ): StreamVideoClient {
    const { apiKey, user, token, tokenProvider } = args;
    if (!user.id && user.type !== 'anonymous') {
      throw new Error('user.id is required for a non-anonymous user');
    }

    if (
      !token &&
      !tokenProvider &&
      user.type !== 'anonymous' &&
      user.type !== 'guest'
    ) {
      throw new Error(
        'tokenProvider or token is required for a authenticated users',
      );
    }

    return (
      StreamVideoClient._instances.get(getInstanceKey(apiKey, user)) ||
      new StreamVideoClient(args)
    );
  }

  private registerClientInstance = (apiKey: string, user: User) => {
    const instanceKey = getInstanceKey(apiKey, user);
    if (StreamVideoClient._instances.has(instanceKey)) {
      this.logger.warn(
        `A StreamVideoClient already exists for ${user.id}; Prefer using getOrCreateInstance method`,
      );
    }
    StreamVideoClient._instances.set(instanceKey, this);
  };

  private registerEffects = () => {
    if (this.effectsRegistered) return;

    this.eventHandlersToUnregister.push(
      this.on('call.created', (event) => this.initCallFromEvent(event)),
      this.on('call.ring', (event) => this.initCallFromEvent(event)),
      this.on('connection.changed', (event) => {
        if (!event.online) return;

        const callsToReWatch = this.state.calls
          .filter((call) => call.watching)
          .map((call) => call.cid);
        if (callsToReWatch.length <= 0) return;

        this.rewatchCalls(callsToReWatch).catch((err) => {
          this.logger.error('Failed to re-watch calls', err);
        });
      }),
    );

    this.effectsRegistered = true;
  };

  /**
   * Initializes a call from a call created or ringing event.
   * @param e the event.
   */
  private initCallFromEvent = async (e: CallCreatedEvent | CallRingEvent) => {
    try {
      const concurrencyTag = getCallInitConcurrencyTag(e.call_cid);
      await withoutConcurrency(concurrencyTag, async () => {
        const ringing = e.type === 'call.ring';
        let call = this.state.findCall(e.call.type, e.call.id);
        if (call) {
          if (ringing) {
            if (this.shouldRejectCall(call.cid)) {
              this.logger.info(
                `Leaving call with busy reject reason ${call.cid} because user is busy`,
              );
              // remove the instance from the state store
              await call.leave();
              // explicitly reject the call with busy reason as calling state was not ringing before and leave would not call it therefore
              await call.reject({ reason: 'busy' });
            } else {
              await call.updateFromRingingEvent(e as CallRingEvent);
              await call.get();
            }
          } else {
            call.state.updateFromCallResponse(e.call);
          }
          return;
        }

        call = new Call({
          videoApi: this.api,
          streamClient: this.streamClient,
          clientEventReporter: this.clientEventReporter,
          type: e.call.type,
          id: e.call.id,
          members: e.members,
          clientState: this.state,
          ringing,
        });

        if (ringing) {
          if (this.shouldRejectCall(call.cid)) {
            this.logger.info(`Rejecting call ${call.cid} because user is busy`);
            // call is not in the state store yet, so just reject api is enough
            await call.reject({ reason: 'busy' });
          } else {
            await call.updateFromRingingEvent(e as CallRingEvent);
            await call.get();
          }
        } else {
          call.state.updateFromCallResponse(e.call);
          this.state.registerCall(call);
          this.logger.info(`New call created and registered: ${call.cid}`);
        }
      });
    } catch (err) {
      this.logger.error(`Failed to init call from event ${e.type}`, err);
    }
  };

  /**
   * Queries per-call stats.
   *
   * @param data the query data.
   */
  queryCallStats = (data: QueryCallStatsRequest = {}) => {
    return this.api.queryCallStats(data);
  };

  /**
   * Queries aggregated call stats.
   *
   * @param data the query data.
   */
  queryAggregateCallStats = (data: QueryAggregateCallStatsRequest = {}) => {
    return this.api.queryAggregateCallStats(data);
  };

  /**
   * Queries the API for calls matching the given filters.
   * @param data the query data.
   */
  private doQueryCalls = (data: QueryCallsRequest) => {
    return this.api.queryCalls(data);
  };

  /**
   * Rewatches the given calls with retry logic.
   * @param callsToReWatch array of call IDs to rewatch
   */
  private rewatchCalls = async (callsToReWatch: string[]): Promise<void> => {
    this.logger.info(`Rewatching calls ${callsToReWatch.join(', ')}`);
    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        this.logger.info(
          `Rewatching calls ${callsToReWatch.join(', ')} attempt ${attempt + 1}`,
        );

        const response = await this.doQueryCalls({
          watch: true,
          filter_conditions: { cid: { $in: callsToReWatch } },
        });

        for (const c of response.calls) {
          const call = this.state.findCall(c.call.type, c.call.id);

          if (call) {
            call.updateFromCallStateResponse(c);
            // ring events missed while the WS was down aren't replayed
            reconcileRingState(call, 'ring-poll-api').catch((err) => {
              call.logger.error(
                'Failed to reconcile the ring state after rewatch',
                err,
              );
            });
          }
        }

        return;
      } catch (err) {
        if (err instanceof ErrorFromResponse && err.unrecoverable) {
          throw err;
        }

        this.logger.warn(
          `Failed to re-watch calls (attempt ${attempt + 1}/${maxRetries}), retrying.`,
          err,
        );

        if (attempt === maxRetries - 1) {
          throw err;
        }
      }

      await sleep(retryInterval(attempt));
    }
  };

  /**
   * Connects the given user to the client.
   *
   * @param user the user to connect.
   * @param tokenOrProvider a token or a function that returns a token.
   */
  connectUser = async (
    user: User,
    tokenOrProvider?: TokenOrProvider,
  ): Promise<void | ConnectedEvent> => {
    if (user.type === 'anonymous') {
      user.id = '!anon';
      return this.connectAnonymousUser(user as UserWithId, tokenOrProvider);
    }

    const reporter = this.clientEventReporter;
    reporter.startCoordinatorConnection(user.id);

    const connectUserResponse = await withoutConcurrency(
      this.connectionConcurrencyTag,
      async () => {
        const client = this.streamClient;
        const { onConnectUserError, persistUserOnConnectionFailure } =
          client.options;
        let { maxConnectUserRetries = 5 } = client.options;
        maxConnectUserRetries = Math.max(maxConnectUserRetries, 1);

        const errorQueue: Error[] = [];
        for (let attempt = 0; attempt < maxConnectUserRetries; attempt++) {
          try {
            this.logger.trace(`Connecting user (${attempt})`, user);
            return await reporter.trackCoordinatorWs(() =>
              user.type === 'guest'
                ? client.connectGuestUser(user)
                : client.connectUser(user, tokenOrProvider),
            );
          } catch (err) {
            this.logger.warn(`Failed to connect a user (${attempt})`, err);
            errorQueue.push(err as Error);
            if (attempt === maxConnectUserRetries - 1) {
              reporter.closeCoordinatorWs();
              onConnectUserError?.(err as Error, errorQueue);
              throw err;
            }

            // we need to force to disconnect the user if the client is
            // configured to persist the user on connection failure
            if (persistUserOnConnectionFailure) {
              await client.disconnectUser();
            }

            await sleep(retryInterval(attempt));
          }
        }
      },
    );

    // connectUserResponse will be void if connectUser called twice for the same user
    if (connectUserResponse?.me) {
      this.state.setConnectedUser(connectUserResponse.me);
    }

    this.registerEffects();

    return connectUserResponse;
  };

  /**
   * Leaves every call this client still tracks. Runs while the coordinator
   * connection is alive, so that the leave requests each call sends reach the
   * backend.
   */
  private leaveAllCalls = async () => {
    for (const call of this.state.calls) {
      if (call.state.callingState === CallingState.LEFT) continue;

      this.logger.info(`User disconnected, leaving call: ${call.cid}`);
      await call
        .leave({ message: 'client.disconnectUser() called' })
        .catch((err) => {
          this.logger.error(`Error leaving call: ${call.cid}`, err);
        });
    }
  };

  /**
   * Disconnects the currently connected user from the client.
   * Leaves every call the user is still in, and updates `client.state`accordingly.
   *
   * @param timeout Max number of ms, to wait for close event of websocket, before forcefully assuming successful disconnection.
   */
  disconnectUser = async (timeout?: number) => {
    await withoutConcurrency(this.connectionConcurrencyTag, async () => {
      const { user, key } = this.streamClient;
      if (!user) return;

      await this.leaveAllCalls();
      await this.streamClient.disconnectUser(timeout);

      if (user.id) {
        StreamVideoClient._instances.delete(getInstanceKey(key, user));
      }
      this.eventHandlersToUnregister.forEach((unregister) => unregister());
      this.eventHandlersToUnregister = [];
      this.effectsRegistered = false;
      this.state.setConnectedUser(undefined);
    });
  };

  /**
   * You can subscribe to WebSocket events provided by the API.
   * To remove a subscription, call the `off` method or, execute the returned unsubscribe function.
   * Please note that subscribing to WebSocket events is an advanced use-case,
   * for most use-cases it should be enough to watch for changes in the reactive
   * `client.state`.
   *
   * @param eventName the event name or 'all'.
   * @param callback the callback which will be called when the event is emitted.
   * @returns an unsubscribe function.
   */
  on = <E extends keyof AllClientEvents>(
    eventName: E,
    callback: ClientEventListener<E>,
  ) => {
    return this.streamClient.on(eventName, callback);
  };

  /**
   * Remove subscription for WebSocket events that were created by the `on` method.
   *
   * @param eventName the event name.
   * @param callback the callback which was passed to the `on` method.
   */
  off = <E extends keyof AllClientEvents>(
    eventName: E,
    callback: ClientEventListener<E>,
  ) => {
    return this.streamClient.off(eventName, callback);
  };

  /**
   * Creates a new call.
   *
   * @param type the type of the call.
   * @param id the id of the call.
   * @param options additional options for call creation.
   */
  call = (
    type: string,
    id: string,
    options: { reuseInstance?: boolean } = {},
  ) => {
    const call = options.reuseInstance
      ? this.state.findCall(type, id)
      : undefined;
    return (
      call ??
      new Call({
        videoApi: this.api,
        streamClient: this.streamClient,
        clientEventReporter: this.clientEventReporter,
        id: id,
        type: type,
        clientState: this.state,
      })
    );
  };

  /**
   * Creates a new guest user with the given data.
   *
   * @param data the data for the guest user.
   */
  createGuestUser = async (data: CreateGuestRequest) => {
    return this.streamClient.createGuestUser(data);
  };

  /**
   * Queries calls and returns them as live {@link Call} instances, applying the
   * device config and, with `watch: true`, setting up and registering each one.
   *
   * @param data the query data.
   * @param opts additional options, for tweaking the API behavior.
   */
  queryCalls = async (
    data: QueryCallsRequest = {},
    opts: { withDisabledDevices?: boolean } = {},
  ) => {
    const { withDisabledDevices = true } = opts;
    const response = await this.doQueryCalls(data);
    const calls = [];
    for (const c of response.calls) {
      const call = new Call({
        videoApi: this.api,
        streamClient: this.streamClient,
        clientEventReporter: this.clientEventReporter,
        id: c.call.id,
        type: c.call.type,
        members: c.members,
        ownCapabilities: c.own_capabilities,
        watching: data.watch,
        clientState: this.state,
      });
      call.state.updateFromCallResponse(c.call);
      await call.applyDeviceConfig(c.call.settings, {
        publish: false,
        withDisabledDevices,
      });
      if (data.watch) {
        await call.setup();
        this.state.registerCall(call);
      }
      calls.push(call);
    }
    return {
      ...response,
      calls: calls,
    };
  };

  /**
   * Returns a list of available data centers available for hosting calls.
   */
  edges = async () => {
    return this.api.getEdges();
  };

  /**
   * Adds a push device for the connected user.
   *
   * @param data the device token, push provider, and optional device settings.
   */
  addDevice = async (data: CreateDeviceRequest) => {
    return await this.apiClient.sendRequest<Response>(
      'POST',
      DEVICES_PATH,
      undefined,
      undefined,
      data,
    );
  };

  /**
   * Adds a VoIP push device for the connected user.
   *
   * @param data the device token, push provider, and optional device settings.
   */
  addVoipDevice = async (data: Omit<CreateDeviceRequest, 'voip_token'>) => {
    return await this.addDevice({ ...data, voip_token: true });
  };

  /**
   * getDevices - Returns the devices associated with the current user.
   */
  getDevices = async () => {
    return await this.apiClient.sendRequest<ListDevicesResponse>(
      'GET',
      DEVICES_PATH,
    );
  };

  /**
   * removeDevice - Removes the device with the given id.
   */
  removeDevice = async ({ id }: Pick<CreateDeviceRequest, 'id'>) => {
    return await this.apiClient.sendRequest<Response>(
      'DELETE',
      DEVICES_PATH,
      undefined,
      { id },
    );
  };

  /**
   * A callback that can be used to create ringing calls from push notifications. If the call already exists, it will do nothing.
   * @param call_cid
   * @returns
   */
  onRingingCall = async (call_cid: string) => {
    return withoutConcurrency(getCallInitConcurrencyTag(call_cid), async () => {
      // if we find the call and is already ringing, we don't need to create a new call
      // as client would have received the call.ring state because the app had WS alive when receiving push notifications
      let call = this.state.calls.find((c) => c.cid === call_cid && c.ringing);
      if (!call) {
        // if not it means that WS is not alive when receiving the push notifications and we need to fetch the call
        const [callType, callId] = call_cid.split(':');
        call = new Call({
          videoApi: this.api,
          streamClient: this.streamClient,
          clientEventReporter: this.clientEventReporter,
          type: callType,
          id: callId,
          clientState: this.state,
          ringing: true,
        });
        await call.get();
      }
      return call;
    });
  };

  /**
   * Connects the given anonymous user to the client.
   *
   * @param user the user to connect.
   * @param tokenOrProvider a token or a function that returns a token.
   */
  private connectAnonymousUser = async (
    user: UserWithId,
    tokenOrProvider: TokenOrProvider,
  ) => {
    return withoutConcurrency(this.connectionConcurrencyTag, () =>
      this.streamClient.connectAnonymousUser(user, tokenOrProvider),
    );
  };

  private shouldRejectCall = (currentCallId: string) => {
    if (!this.rejectCallWhenBusy) return false;
    return this.state.calls.some(
      (c) =>
        c.cid !== currentCallId &&
        c.ringing &&
        c.state.callingState !== CallingState.IDLE &&
        c.state.callingState !== CallingState.LEFT &&
        c.state.callingState !== CallingState.RECONNECTING_FAILED,
    );
  };
}
